import type {
  SmartCreationBrowserDraft,
  SmartCreationDraft,
  SmartCreationReviewWorkspace,
  SmartCreationStructure,
  SmartCreationStructureOptionsEvaluation,
} from "@adpilot/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { ReviewEditQueue } from "@/components/smart-creation/review-edit-queue";
import type { StructureSelectionQueue } from "@/components/smart-creation/structure-selection-queue";
import { fetchCampaignCreationCapabilities } from "@/lib/campaign-creation-api";
import { fetchSmartCreationPlan } from "@/lib/smart-creation-api";
import {
  clearSmartCreationLocalState,
  readSmartCreationLocalState,
  writeSmartCreationLocalState,
} from "@/lib/smart-creation-local-state";
import { draftsMatch } from "./draft-rules";
import {
  type DurableActionIdentity,
  isRevisionConflict,
  retryTransientPlanRead,
  smartCreationPlanQueryKey,
  structureEvaluationQueryKey,
  transientPlanRetryDelay,
} from "./plan-queries";
import { usePlanIdentityReset } from "./use-plan-identity-reset";

const REVISION_CONFLICT_TOAST_ID = "smart-creation-revision-conflict";

interface PlanSessionOptions {
  open: boolean;
  planId: string;
  shopId: string;
  canEdit: boolean;
}

/**
 * Owns the Plan being edited: the server Plan, the browser draft layered over it, the
 * revision-conflict state, and the action lock that serializes durable Plan commands.
 */
export function usePlanSession({ open, planId, shopId, canEdit }: PlanSessionOptions) {
  const { t } = useTranslation("smartCreation");
  const queryClient = useQueryClient();
  const identity = `${shopId}:${planId}`;
  const [draft, setDraft] = useState<SmartCreationDraft | null>(null);
  const [actionSerializing, setActionSerializing] = useState(false);
  const [contentBlocking, setContentBlocking] = useState(false);
  const [revisionConflict, setRevisionConflict] = useState(false);
  const [conflictingPlan, setConflictingPlan] = useState<SmartCreationBrowserDraft | null>(null);
  const [durableActionError, setDurableActionError] = useState<unknown>(null);
  const [strategySubviewDirty, setStrategySubviewDirty] = useState(false);
  const [reviewDirty, setReviewDirty] = useState(false);
  const [reviewSaving, setReviewSaving] = useState(false);
  const [structureSaving, setStructureSaving] = useState(false);
  const [optimisticReview, setOptimisticReview] = useState<{
    identity: string;
    workspace: SmartCreationReviewWorkspace;
  } | null>(null);
  const [strategyEditorEpoch, setStrategyEditorEpoch] = useState(0);
  const structureQueueRef = useRef<StructureSelectionQueue | null>(null);
  const reviewQueueRef = useRef<ReviewEditQueue | null>(null);
  const actionLock = useRef(false);
  const currentIdentityRef = useRef(identity);
  const draftRef = useRef<SmartCreationDraft | null>(null);
  const browserDraftRef = useRef<SmartCreationBrowserDraft | null>(null);
  const localChangesRef = useRef(false);
  const strategySubviewDirtyRef = useRef(false);
  const editVersionRef = useRef(0);
  const revisionConflictRef = useRef(false);
  const mountedRef = useRef(true);
  const handleReviewDirtyChange = useCallback((nextDirty: boolean) => {
    setReviewDirty(nextDirty);
  }, []);
  const handleStrategySubviewDirtyChange = useCallback((nextDirty: boolean) => {
    strategySubviewDirtyRef.current = nextDirty;
    setStrategySubviewDirty(nextDirty);
  }, []);

  const planQuery = useQuery({
    queryKey: smartCreationPlanQueryKey(shopId, planId),
    queryFn: () => fetchSmartCreationPlan(planId, shopId),
    enabled: open,
    retry: retryTransientPlanRead,
    retryDelay: transientPlanRetryDelay,
  });
  const queriedPlan = planQuery.data;
  const browserReviewIsNewer = Boolean(
    browserDraftRef.current?.id === planId &&
      browserDraftRef.current.shopId === shopId &&
      browserDraftRef.current.reviewWorkspace &&
      queriedPlan?.reviewWorkspace &&
      browserDraftRef.current.revision === queriedPlan.revision &&
      browserDraftRef.current.reviewWorkspace.workspaceRevision >
        queriedPlan.reviewWorkspace.workspaceRevision,
  );
  const currentBrowserDraft =
    strategySubviewDirty ||
    strategySubviewDirtyRef.current ||
    reviewDirty ||
    reviewSaving ||
    structureSaving ||
    browserReviewIsNewer
      ? (browserDraftRef.current ?? queriedPlan)
      : queriedPlan;
  const plan =
    currentBrowserDraft && optimisticReview?.identity === identity
      ? { ...currentBrowserDraft, reviewWorkspace: optimisticReview.workspace }
      : currentBrowserDraft;
  const planShopId = plan?.shopId ?? shopId;
  const capabilitiesRequired = Boolean(
    canEdit &&
      (plan?.status === "draft" || plan?.status === "preview_ready") &&
      (plan.draft.currentStep === "preview" ||
        plan.draft.currentStep === "products" ||
        plan.draft.currentStep === "objectives" ||
        plan.draft.currentStep === "strategy" ||
        plan.draft.currentStep === "structure"),
  );
  const capabilitiesQuery = useQuery({
    queryKey: ["campaign-creation", "capabilities", planShopId],
    queryFn: () => fetchCampaignCreationCapabilities(planShopId),
    enabled: open && capabilitiesRequired,
    retry: retryTransientPlanRead,
    retryDelay: transientPlanRetryDelay,
  });

  usePlanIdentityReset(identity, () => {
    currentIdentityRef.current = identity;
    reviewQueueRef.current?.reset();
    structureQueueRef.current?.reset();
    editVersionRef.current += 1;
    localChangesRef.current = false;
    draftRef.current = null;
    browserDraftRef.current = null;
    actionLock.current = false;
    setDraft(null);
    setRevisionConflict(false);
    setConflictingPlan(null);
    setActionSerializing(false);
    setContentBlocking(false);
    setDurableActionError(null);
    setStrategySubviewDirty(false);
    handleReviewDirtyChange(false);
    strategySubviewDirtyRef.current = false;
    setStrategyEditorEpoch((epoch) => epoch + 1);
    revisionConflictRef.current = false;
  });

  useEffect(() => {
    if (!queriedPlan) return;
    if (
      localChangesRef.current ||
      strategySubviewDirty ||
      strategySubviewDirtyRef.current ||
      reviewDirty ||
      reviewSaving ||
      structureQueueRef.current?.isPending()
    )
      return;
    if (browserDraftRef.current && queriedPlan.revision < browserDraftRef.current.revision) return;
    if (browserReviewIsNewer) return;
    const cachedDraft = readSmartCreationLocalState(
      queriedPlan.shopId,
      queriedPlan.id,
      queriedPlan.revision,
    );
    const nextDraft = cachedDraft ?? queriedPlan.draft;
    browserDraftRef.current = queriedPlan;
    localChangesRef.current = !draftsMatch(nextDraft, queriedPlan.draft);
    draftRef.current = nextDraft;
    setDraft(nextDraft);
  }, [queriedPlan, reviewDirty, reviewSaving, browserReviewIsNewer, strategySubviewDirty]);

  useEffect(() => {
    mountedRef.current = true;
    currentIdentityRef.current = `${shopId}:${planId}`;
    return () => {
      mountedRef.current = false;
      currentIdentityRef.current = "";
    };
  }, [planId, shopId]);

  // Saving the Structure step and generating its preview are separate durable commands.
  // If preview generation fails, the current revision has no editable Review Workspace
  // and must retry from Structure rather than rendering an old projection.
  const awaitingGeneratePreview = Boolean(
    (plan?.status === "draft" || plan?.status === "preview_ready") &&
      plan.draft.currentStep === "preview" &&
      (!plan.reviewWorkspace || plan.reviewWorkspace.basedOn.planRevision !== plan.revision),
  );
  const currentStep = awaitingGeneratePreview ? "structure" : (draft?.currentStep ?? "products");
  const displayedDraft =
    awaitingGeneratePreview && draft ? { ...draft, currentStep: "structure" as const } : draft;

  const setPlan = useCallback(
    (next: SmartCreationBrowserDraft, latestStructure?: SmartCreationStructure) => {
      const previous = browserDraftRef.current;
      if (
        latestStructure &&
        previous &&
        draftsMatch({ ...previous.draft, structure: next.draft.structure }, next.draft)
      ) {
        const cached = queryClient.getQueryData<SmartCreationStructureOptionsEvaluation>(
          structureEvaluationQueryKey(previous.shopId, previous.id, previous.revision),
        );
        if (cached)
          queryClient.setQueryData(
            structureEvaluationQueryKey(next.shopId, next.id, next.revision),
            cached,
          );
      }
      const nextDraft = latestStructure
        ? { ...next.draft, structure: latestStructure }
        : next.draft;
      browserDraftRef.current = next;
      setRevisionConflict(false);
      setConflictingPlan(null);
      setDurableActionError(null);
      revisionConflictRef.current = false;
      localChangesRef.current = !draftsMatch(nextDraft, next.draft);
      if (localChangesRef.current) {
        if (!writeSmartCreationLocalState(next.shopId, next.id, next.revision, nextDraft))
          toast.warning(t("errors.localStorage"), { id: "smart-creation-storage" });
      } else clearSmartCreationLocalState(next.shopId, next.id);
      draftRef.current = nextDraft;
      queryClient.setQueryData(smartCreationPlanQueryKey(next.shopId, next.id), next);
      setDraft(nextDraft);
    },
    [queryClient, t],
  );

  const handleRevisionConflict = useCallback(
    async (error: unknown, request: DurableActionIdentity): Promise<boolean> => {
      if (!(await isRevisionConflict(error))) return false;
      if (request.identity !== currentIdentityRef.current) return true;
      if (revisionConflictRef.current) return true;
      revisionConflictRef.current = true;
      editVersionRef.current += 1;
      setRevisionConflict(true);
      setConflictingPlan(null);

      const latestBrowserDraft = await fetchSmartCreationPlan(request.planId, request.shopId).catch(
        () => null,
      );
      if (!mountedRef.current || request.identity !== currentIdentityRef.current) return true;
      if (latestBrowserDraft) {
        queryClient.setQueryData(
          smartCreationPlanQueryKey(request.shopId, request.planId),
          latestBrowserDraft,
        );
        setConflictingPlan(latestBrowserDraft);
      }
      return true;
    },
    [queryClient],
  );

  const handleDurableActionError = useCallback(
    async (error: unknown, request: DurableActionIdentity): Promise<boolean> => {
      const conflictHandled = await handleRevisionConflict(error, request);
      if (
        !conflictHandled &&
        mountedRef.current &&
        request.identity === currentIdentityRef.current
      ) {
        setDurableActionError(error);
      }
      return conflictHandled;
    },
    [handleRevisionConflict],
  );

  const reloadConflictingPlan = useCallback(async () => {
    const request = {
      identity: currentIdentityRef.current,
      planId,
      shopId,
    };
    const latestBrowserDraft =
      conflictingPlan ??
      (await fetchSmartCreationPlan(request.planId, request.shopId).catch(() => null));
    if (latestBrowserDraft) {
      queryClient.setQueryData(
        smartCreationPlanQueryKey(request.shopId, request.planId),
        latestBrowserDraft,
      );
    }
    if (!mountedRef.current || request.identity !== currentIdentityRef.current) return;
    if (!latestBrowserDraft) {
      toast.error(t("conflict.reloadError"));
      return;
    }
    editVersionRef.current += 1;
    reviewQueueRef.current?.reset();
    structureQueueRef.current?.reset();
    strategySubviewDirtyRef.current = false;
    setStrategySubviewDirty(false);
    handleReviewDirtyChange(false);
    setStrategyEditorEpoch((epoch) => epoch + 1);
    browserDraftRef.current = latestBrowserDraft;
    localChangesRef.current = false;
    clearSmartCreationLocalState(latestBrowserDraft.shopId, latestBrowserDraft.id);
    draftRef.current = latestBrowserDraft.draft;
    setDraft(latestBrowserDraft.draft);
    setRevisionConflict(false);
    setConflictingPlan(null);
    revisionConflictRef.current = false;
  }, [conflictingPlan, handleReviewDirtyChange, planId, queryClient, shopId, t]);

  // The sticky conflict toast is the only way back to an editable Plan after a conflict.
  useEffect(() => {
    if (!revisionConflict) {
      toast.dismiss?.(REVISION_CONFLICT_TOAST_ID);
      return;
    }
    toast.error(t("conflict.title"), {
      id: REVISION_CONFLICT_TOAST_ID,
      description: t("conflict.description"),
      duration: Number.POSITIVE_INFINITY,
      action: {
        label: t("conflict.reload"),
        onClick: () => void reloadConflictingPlan(),
      },
    });
  }, [reloadConflictingPlan, revisionConflict, t]);
  useEffect(() => () => void toast.dismiss?.(REVISION_CONFLICT_TOAST_ID), []);

  const releaseActionLock = (lockIdentity: string) => {
    if (!mountedRef.current || lockIdentity !== currentIdentityRef.current) return;
    actionLock.current = false;
    setActionSerializing(false);
    setContentBlocking(false);
  };

  return {
    identity,
    planQuery,
    queriedPlan,
    capabilitiesQuery,
    capabilitiesRequired,
    plan,
    planShopId,
    draft,
    setDraft,
    displayedDraft,
    currentStep,
    awaitingGeneratePreview,
    actionSerializing,
    setActionSerializing,
    contentBlocking,
    setContentBlocking,
    revisionConflict,
    setRevisionConflict,
    conflictingPlan,
    setConflictingPlan,
    durableActionError,
    setDurableActionError,
    strategySubviewDirty,
    setStrategySubviewDirty,
    reviewDirty,
    reviewSaving,
    setReviewSaving,
    structureSaving,
    setStructureSaving,
    setOptimisticReview,
    strategyEditorEpoch,
    setStrategyEditorEpoch,
    structureQueueRef,
    reviewQueueRef,
    actionLock,
    currentIdentityRef,
    draftRef,
    browserDraftRef,
    localChangesRef,
    strategySubviewDirtyRef,
    editVersionRef,
    revisionConflictRef,
    mountedRef,
    handleReviewDirtyChange,
    handleStrategySubviewDirtyChange,
    setPlan,
    handleRevisionConflict,
    handleDurableActionError,
    releaseActionLock,
  };
}

export type PlanSession = ReturnType<typeof usePlanSession>;
